import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import Table from 'components/ReusableComponents/Table'
import moment from 'moment'
import { useState } from 'react'
import { formatPrice } from 'utils/common'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './CampaignAnalysis.module.scss'

const campaignNames = {
  AfterLastVisit: 'After last visit',
  Lost3Months: 'Lost 90%',
  Lost6Months: 'Lost 80%',
  Lost9Months: 'Lost 60%',
  Lost12Months: 'Lost 40%',
  Lost15Months: 'Lost 20%',
  Cancelled: 'Cancelled',
  NoShow: 'No show',
  BookingUpdated: 'Booking updated',
  CheckedOutReview: 'Review',
  ConsentGiven: 'Opt-in',
  OptInRequest: 'Opt-in',
  ServiceStartDayBefore: 'Day before check-in',
  ServiceStartForPrimaryGuest: 'During check-in',
  ServiceStartForSecondaryGuest: 'Additional guest check-in',
  ServiceRestart: 'Restart messaging',
  JustBooked: 'Just booked',
  OTAJustBooked: 'OTA Just booked',
  OTAAfterVisit: 'OTA After visit'
}

const data = [
  {
    period: '2022-08-01',
    campaign: 'Lost3Months',
    texts: '24124',
    cost: '150.00',
    converted: '2245',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '31546',
    revenueFactor: '75.2'
  },
  {
    period: '2022-09-01',
    campaign: 'Lost3Months',
    texts: '24124',
    cost: '150.00',
    converted: '2245',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '31546',
    revenueFactor: '75.2'
  },
  {
    period: '2022-08-01',
    campaign: 'Lost6Months',
    texts: '24124',
    cost: '72.20',
    converted: '912',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '2424',
    revenueFactor: '45.2'
  },
  {
    period: '2022-09-01',
    campaign: 'Lost6Months',
    texts: '24124',
    cost: '72.20',
    converted: '912',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '2424',
    revenueFactor: '45.2'
  },
  {
    period: '2022-8-01',
    campaign: 'Lost9Months',
    texts: '24124',
    cost: '50.20',
    converted: '859',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '60002',
    revenueFactor: '25.2'
  },
  {
    period: '2022-9-01',
    campaign: 'Lost9Months',
    texts: '24124',
    cost: '50.20',
    converted: '859',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '60002',
    revenueFactor: '25.2'
  },
  {
    period: '2022-8-01',
    campaign: 'Lost12Months',
    texts: '24124',
    cost: '72.20',
    converted: '2245',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '25125',
    revenueFactor: '35.2'
  },
  {
    period: '2022-9-01',
    campaign: 'Lost12Months',
    texts: '24124',
    cost: '72.20',
    converted: '2245',
    conversion: '4.45',
    convertedRevenue: '75.242',
    invoicedBookings: '1245',
    invoiceRatio: '85',
    commissionTotal: '23654',
    invoicedHotelRevenue: '520654',
    netRevenue: '25125',
    revenueFactor: '35.2'
  }
]

const formatCurrencyWithoutDecimals = currency =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(currency)

const Cell = ({ children, isColored, ...props }) => (
  <Table.Cell className={isColored ? styles.cellColored : styles.cell} {...props}>
    {children}
  </Table.Cell>
)

const TitleCell = ({ children, ...props }) => (
  <Table.TitleCell {...props}>
    <div className={styles.titleCell}>{children}</div>
  </Table.TitleCell>
)

const CampaignAnalysis = () => {
  const [sortIndex, setSortIndex] = useState(0)
  const [sortAscending, setSortAscending] = useState(false)

  const [selectedCampaigns, setSelectedCampaigns] = useState(Object.keys(campaignNames))

  function handleTitleClick(i) {
    if (sortIndex === i) {
      setSortAscending(d => !d)
    } else {
      setSortIndex(i)
    }
  }

  const periods = [...new Set(data.map(d => d.period))].sort().reduce((acc, period) => {
    return { ...acc, [period]: period }
  }, {})

  const [selectedPeriods, setSelectedPeriods] = useState(Object.keys(periods))

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          return a.campaign - b.campaign

        case 1:
          return new Date(a.period) - new Date(b.period)

        case 2:
          return a.texts - b.texts

        case 3:
          return a.cost - b.cost

        case 4:
          return a.converted - b.converted

        case 5:
          return a.conversion - b.conversion

        case 6:
          return a.convertedRevenue - b.convertedRevenue

        case 7:
          return 0

        case 8:
          return a.invoicedBookings - b.invoicedBookings

        case 9:
          return a.invoiceRatio - b.invoiceRatio

        case 10:
          return a.commissionTotal - b.commissionTotal

        case 11:
          return a.invoicedHotelRevenue - b.invoicedHotelRevenue

        case 12:
          return 1

        case 13:
          return a.netRevenue - b.netRevenue

        case 14:
          return a.revenueFactor - b.revenueFactor

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.filterBar}>
        <MultiSelectDropdown
          options={campaignNames}
          value={selectedCampaigns}
          anyText="All"
          itemName="campaigns"
          itemNamePlural="campaigns"
          onChangeValue={setSelectedCampaigns}
          className={styles.statusFilter}
          hasUnselectButton
        />
        <MultiSelectDropdown
          options={periods}
          value={selectedPeriods}
          anyText="All"
          itemName="periods"
          itemNamePlural="periods"
          onChangeValue={setSelectedPeriods}
          className={styles.statusFilter}
          hasUnselectButton
        />
      </div>
      <CustomHeader title="Campaign Analysis" width={1080} />
      <Table
        widths={[100, 90, 80, 80, 80, 80, 80, 20, 80, 75, 85, 84, 20, 80, 80]}
        handleTitleCellClick={handleTitleClick}
        sortIndex={sortIndex}
        sortDirection={sortAscending ? 'up' : 'down'}
      >
        <Table.TitleRow className={styles.titleRow}>
          <TitleCell>Campaign</TitleCell>
          <TitleCell>Period</TitleCell>
          <TitleCell>Texts</TitleCell>
          <TitleCell>Cost</TitleCell>
          <TitleCell>Converted</TitleCell>
          <TitleCell>Conversion</TitleCell>
          <TitleCell>
            Converted
            <br />
            revenue
          </TitleCell>
          <TitleCell></TitleCell>
          <TitleCell>
            Invoiced
            <br />
            bookings
          </TitleCell>
          <TitleCell>
            Invoice
            <br />
            ratio
          </TitleCell>
          <TitleCell>
            Total
            <br />
            commission
          </TitleCell>
          <TitleCell>
            Invoiced
            <br />
            hotel rev.
          </TitleCell>
          <TitleCell></TitleCell>
          <TitleCell>
            Net
            <br />
            revenue
          </TitleCell>
          <TitleCell>
            Revenue
            <br />
            factor
          </TitleCell>
        </Table.TitleRow>
        {sortResults(data)
          .filter(d => selectedCampaigns.includes(d.campaign))
          .filter(d => selectedPeriods.includes(d.period))
          .map(row => (
            <Table.Row key={`${row.campaign}${row.period}`} className={styles.dataRow}>
              <Cell>
                <span className={styles.campaignName}>{campaignNames[row.campaign]}</span>
              </Cell>
              <Cell>{moment(row.period).format('MMM YYYY')}</Cell>
              <Cell>{formatNumberAsThousands(row.texts)}</Cell>
              <Cell>{formatPrice(row.cost)}</Cell>
              <Cell>{formatNumberAsThousands(row.converted)}</Cell>
              <Cell>{Number(row.conversion).toFixed(2)}%</Cell>
              <Cell>{formatPrice(row.convertedRevenue)}</Cell>
              <Cell isColored></Cell>
              <Cell isColored>{formatNumberAsThousands(row.invoicedBookings)}</Cell>
              <Cell isColored>{Number(row.invoiceRatio).toFixed(2)}%</Cell>
              <Cell isColored>{formatCurrencyWithoutDecimals(row.commissionTotal)}</Cell>
              <Cell isColored>
                {formatCurrencyWithoutDecimals(row.invoicedHotelRevenue)}
              </Cell>
              <Cell></Cell>
              <Cell>{formatCurrencyWithoutDecimals(row.netRevenue)}</Cell>
              <Cell>{row.revenueFactor}</Cell>
            </Table.Row>
          ))}
      </Table>
    </div>
  )
}

export default CampaignAnalysis
